// ─── Raw payload types (matching atlas_intel.sh output) ───────────────────────

export interface CostRecord {
  snapshot_id?: string
  collected_at?: string
  project_id?: string // Atlas project ID
  c: string // clusterName
  d: string // usageDate (YYYY-MM-01)
  a: number // usageAmount
  s?: string // serviceName
  id?: string // clusterId
}

export interface TierRecord {
  snapshot_id?: string
  collected_at?: string
  project_id?: string // Atlas project ID
  name: string
  id: string
  tier: string
  diskGB: number
  provider: string
  region: string
  nodes: number
  clusterType?: string
}

export interface ServiceCostRecord {
  snapshot_id?: string
  collected_at?: string
  project_id?: string // Atlas project ID
  s: string // service name
  d: string // usageDate
  a: number // usageAmount
}

/** Org-level cost record — aggregated by Atlas at organisation granularity. */
export interface OrgCostRecord {
  org_id?: string
  d: string // usageDate YYYY-MM-01
  a: number // usageAmount
  s: string // serviceName (or 'Total')
}

/** Available cost snapshot for the history picker. */
export interface CostSnapshot {
  snapshotId: string
  collectedAt: string
  projectCount: number
}

export interface StorageRow {
  snapshot_id?: string
  collected_at?: string
  project_id?: string // Atlas project ID
  cluster: string
  database: string
  collection: string
  doc_count: string
  data_size_mb: string
  storage_size_mb: string
  index_size_mb: string
  total_disk_mb: string
  avg_obj_bytes: string
  num_indexes: string
  capped: string
  free_storage_mb: string
  reusable_bytes_mb: string
  // computed
  est_cost?: number
  reads?: number
  writes?: number
  total_ops?: number
}

export interface OpsRow {
  snapshot_id?: string
  collected_at?: string
  project_id?: string // Atlas project ID
  cluster: string
  database: string
  collection: string
  reads: string
  writes: string
  read_ms: string
  write_ms: string
  total_ops: string
}

export interface IndexRow {
  snapshot_id?: string
  collected_at?: string
  project_id?: string // Atlas project ID
  cluster: string
  database: string
  collection: string
  index_name: string
  accesses: string
  since: string
}

export interface ServerRow {
  cluster: string
  insert: string
  query: string
  update: string
  delete: string
  getmore: string
  command: string
  net_bytes_in_mb: string
  net_bytes_out_mb: string
  connections_current: string
  connections_available: string
  /** Milliseconds mongod has been running — from serverStatus().uptimeMillis */
  uptime_millis?: number | string
  /** ISO timestamp when this row was collected */
  collected_at?: string
  /** Populated when read from ClickHouse (not present in CSV file rows) */
  project_id?: string
}

// ─── Delta (live ops/sec) ──────────────────────────────────────────────────────

export interface DeltaCluster {
  cluster: string
  sampledAt?: string
  intervalSec?: number
  queryPerSec: number
  insertPerSec: number
  updatePerSec: number
  deletePerSec: number
  getmorePerSec: number
  commandPerSec: number
  bytesInPerSec: number
  bytesOutPerSec: number
  connCurrent: number
  connAvailable?: number
  source?: string
  error?: string
}

export interface DeltaFile {
  status: string
  sampledAt: string
  clusterCount: number
  successCount: number
  clusters: DeltaCluster[]
}

// ─── Metrics (time-series) ─────────────────────────────────────────────────────

export interface MetricDataPoint {
  ts: string
  v: number
}

export interface MetricSeries {
  name: string
  units: string
  dataPoints: MetricDataPoint[]
}

export interface ClusterMetrics {
  cluster: string
  processId?: string
  fetchedAt: string
  metrics: MetricSeries[]
  error?: string
}

export interface MetricsFile {
  status: string
  fetchedAt: string
  clusterCount: number
  successCount: number
  clusters: ClusterMetrics[]
}

// ─── Deep optimization ─────────────────────────────────────────────────────────

export type RecommendationCategory =
  | 'redundant_index'
  | 'duplicate_index'
  | 'unused_index'
  | 'write_amplification'
  | 'missing_index'
  | 'uncovered_sort'
  | 'plan_cache_thrashing'
  | 'tier_downsize'

export type RecommendationConfidence = 'high' | 'medium' | 'low'

export interface WeightBreakdown {
  weight: number
  weightLabel: string
  estimatedExecPerDay: number
  estimatedDocsScannedPerExec: number
  explanation?: string
}

export interface ESRBreakdown {
  equality: string[]
  sort: string[]
  range: string[]
}

export interface Recommendation {
  id: number
  projectId?: string
  projectName?: string
  snapshotId?: string
  collectedAt?: string
  cluster: string
  namespace: string
  category: RecommendationCategory
  confidence: RecommendationConfidence
  title: string
  detail: string
  action: string
  savingsMonthly: number | null
  impact: string
  // index-specific fields
  dropIndex?: string
  keepIndex?: string
  dropKey?: string
  keepKey?: string
  dropAccesses?: number
  keepAccesses?: number
  indexKey?: string
  daysSinceTracking?: number
  isUnique?: boolean
  isSparse?: boolean
  hasPartial?: boolean
  // query correlation (computed by enrichRecommendation)
  weightNum?: number
  indexShape?: Record<string, number>
  esr?: ESRBreakdown
  queryPattern?: string
  weightBreakdown?: WeightBreakdown
  mongosh_command?: string
}

export interface DeepReportSummary {
  totalRecommendations: number
  byCategory: Record<string, number>
  byConfidence: { high: number; medium: number; low: number }
  estimatedMonthlySavings: number
  generatedAt: string
  clustersScanned: number
}

export interface DeepReport {
  summary: DeepReportSummary
  recommendations: Recommendation[]
}

// ─── Source config ─────────────────────────────────────────────────────────────

export type SourceType = 'gcpBaseUrl' | 'localBaseUrl' | 'localFiles' | 'nestApi'

/** Day-based legacy values are kept for backward compat with stored localStorage keys.
 *  Prefer month-based: '3m' | '6m' | '12m' | 'all' */
export type TimeRange = '7d' | '14d' | '30d' | '90d' | '3m' | '6m' | '12m' | 'all'

export interface SourceProfile {
  id: string
  label: string
  type: SourceType
  baseUrl?: string // for gcpBaseUrl and localBaseUrl
  apiBaseUrl?: string // for nestApi type (relative or absolute)
  fileMap?: Record<AtlasFileKey, string> // for manualUrls
  loadedFiles?: Record<AtlasFileKey, string> // dataUrls for localFiles
  createdAt: string
}

export type AtlasFileKey =
  | 'costs'
  | 'tiers'
  | 'costs_by_service'
  | 'storage'
  | 'ops'
  | 'indexes'
  | 'server'
  | 'delta'
  | 'metrics'
  | 'deep_report'

export const ATLAS_FILE_NAMES: Record<AtlasFileKey, string> = {
  costs: 'costs.json',
  tiers: 'tiers.json',
  costs_by_service: 'costs_by_service.json',
  storage: 'storage.csv',
  ops: 'ops.csv',
  indexes: 'indexes.csv',
  server: 'server.csv',
  delta: 'delta.json',
  metrics: 'metrics.json',
  deep_report: 'deep_report.json'
}

export const REQUIRED_FILES: AtlasFileKey[] = ['costs', 'tiers', 'storage']
export const OPTIONAL_FILES: AtlasFileKey[] = [
  'costs_by_service',
  'ops',
  'indexes',
  'server',
  'delta',
  'metrics',
  'deep_report'
]

// ─── Loaded dataset (post-parse) ──────────────────────────────────────────────

export interface AtlasDataset {
  costs: CostRecord[]
  tiers: TierRecord[]
  costsByService: ServiceCostRecord[]
  orgCosts: OrgCostRecord[]
  /** First day of the current calendar month (YYYY-MM-01) — records with d === this are partial. */
  currentMonthKey?: string
  storage: StorageRow[]
  ops: OpsRow[]
  indexes: IndexRow[]
  server: ServerRow[]
  delta: DeltaFile | null
  metrics: MetricsFile | null
  deepReport: DeepReport | null
  fileStatus: Record<AtlasFileKey, 'ok' | 'missing' | 'error'>
  loadedAt: string
  lastCollectedAt?: string | null
  source?: 'clickhouse' | 'files'
  deferred?: {
    metrics?: boolean
  }
}

// ─── Collector config (mirrors backend CollectConfig) ─────────────────────────

export interface CollectConfig {
  // Credentials are server-side only — UI only sends project selection
  projectIds?: string[] // selected projects (multi)
  projectId?: string // legacy single project
  parallelism?: number // intra-project cluster parallelism (default 10, env: QUERY_AI_ATLAS_PARALLELISM)
  projectParallelism?: number // cross-project parallelism (default 3, env: QUERY_AI_ATLAS_PROJECT_PARALLELISM)
  monthsBack?: number // how many months of cost history to collect (default: 6)
}

export interface AtlasProject {
  id: string
  name: string
}

// ─── Collection failure report (mirrors backend types/collection-report.ts) ───
//
// Surfaced through GET /collect/status (per-job `report` field) and
// GET /collect/report/:jobId. The Admin → Collect tab renders a "Failure
// Report" card from this; users can pick failures, dispatch /collect/retry
// against the same snapshot, and fire /collect/rollup once retries succeed.

export type FailureCategory =
  | 'srv_resolution'
  | 'dns_resolution'
  | 'mongosh_auth'
  | 'mongosh_timeout'
  | 'mongosh_other'
  | 'atlas_api_auth'
  | 'atlas_api_rate_limit'
  | 'atlas_api_other'
  | 'cluster_state_skip'
  | 'permission_rbac'
  | 'clickhouse_insert'
  | 'unknown'

export type FailureSeverity = 'error' | 'warn' | 'skip'

export type FailurePhase =
  | 'tier'
  | 'cost'
  | 'cluster-scan'
  | 'delta'
  | 'metrics'
  | 'deep'
  | 'rollup'
  | 'lock'

export interface RetryHint {
  cluster?: string
  processId?: string
  projectId?: string
  metricNames?: string[]
}

export interface CollectionFailureRecord {
  id: string
  phase: FailurePhase
  category: FailureCategory
  severity: FailureSeverity
  cluster?: string
  projectId?: string
  processId?: string
  message: string
  retryable: boolean
  retryHint?: RetryHint
  occurredAt: string
}

export interface JobReport {
  jobId: string
  snapshotId: string
  startedAt: string
  completedAt?: string
  status: 'running' | 'complete' | 'error' | 'cancelled'
  failures: CollectionFailureRecord[]
  counts: {
    error: number
    warn: number
    skip: number
    byPhase: Record<FailurePhase, number>
    byCategory: Record<FailureCategory, number>
  }
}

// ─── UI helpers ────────────────────────────────────────────────────────────────

export interface FileLoadStatus {
  key: AtlasFileKey
  filename: string
  status: 'ok' | 'missing' | 'error' | 'loading'
  error?: string
}
